<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h2 mb-1 fw-bold">Flight Records</h1>
        <p class="text-muted mb-0">Browse spray records across all organizations</p>
      </div>
    </div>

    <!-- Error Alert -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters Card -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Organization Filter -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Organization</label>
            <select
              class="form-select"
              [(ngModel)]="filterOrganizationId"
              (change)="onFilterChange()"
            >
              <option value="">All Organizations</option>
              <option *ngFor="let org of organizations" [value]="org.id">
                {{ org.name }}
              </option>
            </select>
          </div>

          <!-- Status Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Status</label>
            <select
              class="form-select"
              [(ngModel)]="filterStatus"
              (change)="onFilterChange()"
            >
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <!-- Date Range: From -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">From</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterStartDate"
              (change)="onFilterChange()"
            />
          </div>

          <!-- Date Range: To -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">To</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterEndDate"
              (change)="onFilterChange()"
            />
          </div>

            <!-- Clear Filters Button -->
          @if (showClearFilters()) {
            <div class="row mt-3">
              <div class="col-12">
                <button type="button" class="btn btn-outline-primary" (click)="resetFilters()">
                  <i class="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading flight records...</p>
    </div>

    <!-- Flight Records Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table data-table table-hover mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Organization</th>
                <th>Field / Crop</th>
                <th>Pilot</th>
                <th>Acreage</th>
                <th>Flights</th>
                <th>Status</th>
                <th>Chemicals</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="records.length === 0">
                <td colspan="7" class="text-center py-5 text-muted">
                  <i class="bi bi-journal-text fs-1 d-block mb-2"></i>
                  <span>No flight records found</span>
                </td>
              </tr>
              <tr
                *ngFor="let record of records"
                class="cursor-pointer"
                (click)="viewRecord(record)"
              >
                <td>{{ formatDate(record.applicationDate) }}</td>
                <td>
                  <span class="fw-medium">{{ record.organizationName || '-' }}</span>
                </td>
                <td>
                  <div class="fw-medium">{{ record.fieldName || '-' }}</div>
                  <small class="text-muted">{{ record.cropType || '-' }}</small>
                </td>
                <td>{{ record.pilotName || '-' }}</td>
                <td>{{ record.totalAcreage }} ac</td>
                <!-- FAA §F.2.d roll-up + passive badge. `!= null` so a record
                     with no aircraft shows "—" rather than 0. Display only. -->
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <span *ngIf="record.filedFlights != null">{{ record.filedFlights }}</span>
                    <span *ngIf="record.filedFlights == null" class="text-muted">&mdash;</span>
                    <span *ngIf="record.needsFlightCount"
                          class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
                          title="At least one aircraft has no flight count entered and will be filed as 1">
                      Needs count
                    </span>
                  </div>
                </td>
                <td>
                  <span class="badge" [ngClass]="getStatusBadgeClass(record.status)">
                    {{ getStatusText(record.status) }}
                  </span>
                </td>
                <td>
                  <span class="text-truncate d-inline-block" style="max-width: 150px;">
                    {{ getFirstChemical(record.chemicals) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination Footer -->
      <div class="card-footer d-flex justify-content-between align-items-center" *ngIf="totalRecords > 0">
        <div class="text-muted">
          Showing {{ showingStart }} to {{ showingEnd }} of {{ totalRecords }} records
        </div>
        <nav *ngIf="totalPages > 1">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <button class="page-link" (click)="onPageChange(currentPage - 1)">
                <i class="bi bi-chevron-left"></i>
              </button>
            </li>
            <li
              *ngFor="let page of paginationRange"
              class="page-item"
              [class.active]="page === currentPage"
            >
              <button class="page-link" (click)="onPageChange(page)">{{ page }}</button>
            </li>
            <li class="page-item" [class.disabled]="currentPage === totalPages">
              <button class="page-link" (click)="onPageChange(currentPage + 1)">
                <i class="bi bi-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</div>
